<template>
  <v-container class="hyrox-admin-dashboard py-6">
    <div class="d-flex flex-column flex-md-row justify-space-between align-center mb-6">
      <div class="mb-4 mb-md-0">
        <v-card-title class="white--text text-h4 font-weight-black pa-0 mb-1"> HYROX <span style="color: #00963f">PFT Manager</span> </v-card-title>
        <v-card-subtitle class="white--text pa-0">Übersicht aller Anmeldungen für den 09. Mai</v-card-subtitle>
      </div>

      <div class="d-flex gap-4">
        <v-btn color="grey darken-3" class="white--text mr-3" @click="exportCSV"> <v-icon left>mdi-download</v-icon> CSV Export </v-btn>
        <v-btn color="#00963f" class="white--text pulse-animation" @click="loadData" :loading="loading">
          <v-icon left>mdi-refresh</v-icon> Aktualisieren
        </v-btn>
      </div>
    </div>

    <v-row class="mb-6">
      <v-col cols="12" sm="6" md="3">
        <v-card dark class="stats-card pa-4" color="grey darken-4">
          <div class="subtitle-2 grey--text">Aktive Dokumente</div>
          <div class="text-h4 font-weight-bold white--text">{{ stats.totalActive }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card dark class="stats-card pa-4" color="grey darken-4">
          <div class="subtitle-2 grey--text">Block 1 (09:00 - 12:00)</div>
          <div class="d-flex align-baseline">
            <div class="text-h4 font-weight-bold" :class="stats.headsBlock1 >= 30 ? 'error--text' : 'white--text'">
              {{ stats.headsBlock1 }}
            </div>
            <div class="subtitle-1 grey--text ml-1">/ 30 Köpfe</div>
          </div>
          <v-progress-linear :value="(stats.headsBlock1 / 30) * 100" color="#00963f" class="mt-2"></v-progress-linear>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card dark class="stats-card pa-4" color="grey darken-4">
          <div class="subtitle-2 grey--text">Block 2 (12:00 - 15:00)</div>
          <div class="d-flex align-baseline">
            <div class="text-h4 font-weight-bold" :class="stats.headsBlock2 >= 30 ? 'error--text' : 'white--text'">
              {{ stats.headsBlock2 }}
            </div>
            <div class="subtitle-1 grey--text ml-1">/ 30 Köpfe</div>
          </div>
          <v-progress-linear :value="(stats.headsBlock2 / 30) * 100" color="#00963f" class="mt-2"></v-progress-linear>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card dark class="stats-card pa-4" color="grey darken-4">
          <div class="subtitle-2 grey--text">Stornos gesamt</div>
          <div class="text-h4 font-weight-bold error--text">{{ stats.totalCancelled }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-card dark class="neon-border-card" color="grey darken-4">
      <v-card-title class="d-flex flex-wrap align-center pb-4">
        Teilnehmerliste
        <v-spacer></v-spacer>

        <v-btn-toggle v-model="statusFilter" mandatory color="#00963f" dark class="mr-4 mt-3 mt-md-0" dense>
          <v-btn value="all" small>Alle</v-btn>
          <v-btn value="active" small>Aktiv</v-btn>
          <v-btn value="cancelled" small>Storno</v-btn>
        </v-btn-toggle>

        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Suchen (Name, E-Mail...)"
          single-line
          hide-details
          outlined
          dense
          color="#00963f"
          style="max-width: 300px"
          class="mt-3 mt-md-0"></v-text-field>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="filteredRegistrations"
        :search="search"
        :loading="loading"
        loading-text="Lade Daten..."
        no-data-text="Keine Anmeldungen gefunden"
        dark
        class="transparent-table elevation-0"
        :items-per-page="15">
        <template v-slot:item.status="{ item }">
          <v-chip small :color="item.status === 'active' ? 'success' : 'error'" outlined class="font-weight-bold text-uppercase">
            {{ item.status === "active" ? "Aktiv" : "Storniert" }}
          </v-chip>
        </template>

        <template v-slot:item.block="{ item }">
          <v-chip small color="#00963f" text-color="white" class="font-weight-bold"> Block {{ item.block }} </v-chip>
        </template>
        <template v-slot:item.participant="{ item }">
          <div class="font-weight-bold">{{ item.firstName }} {{ item.lastName }}</div>
          <v-chip
            v-if="!item.isMember"
            small
            :color="item.hasPaid ? 'success' : 'warning'"
            @click="togglePayment(item, 'main')"
            class="mt-1 cursor-pointer">
            {{ item.hasPaid ? "20.- Bezahlt" : "20.- Offen" }}
          </v-chip>
          <v-chip v-else small color="info" outlined class="mt-1">Mitglied</v-chip>
        </template>
        <template v-slot:item.companion="{ item }">
          <div v-if="item.hasCompanion">
            <span class="font-weight-bold" style="color: #00963f"> {{ item.companionFirstName }} {{ item.companionLastName }} </span>
            <div class="text-caption grey--text">{{ item.companionEmail }}</div>
            <v-chip
              v-if="!item.companionIsMember"
              small
              :color="item.companionHasPaid ? 'success' : 'warning'"
              @click="togglePayment(item, 'companion')"
              class="mt-1 cursor-pointer">
              {{ item.companionHasPaid ? "20.- Bezahlt" : "20.- Offen" }}
            </v-chip>
            <v-chip v-else small color="info" outlined class="mt-1">Mitglied</v-chip>
          </div>
          <span v-else class="grey--text">-</span>
        </template>

        <template v-slot:item.registeredAt="{ item }">
          {{ $moment(item.registeredAt).format("DD.MM.YY - HH:mm") }}
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn icon small color="error" @click="confirmDelete(item)" title="Endgültig löschen">
            <v-icon small>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import api from "@/api"

export default {
  name: "HyroxAdminDashboard",
  data() {
    return {
      loading: false,
      search: "",
      statusFilter: "active", // "all", "active", "cancelled"
      registrations: [],
      stats: {
        totalActive: 0,
        totalCancelled: 0,
        headsBlock1: 0,
        headsBlock2: 0,
      },
      headers: [
        { text: "Status", value: "status", sortable: true },
        { text: "Teilnehmer", value: "participant", sortable: false },
        { text: "E-Mail", value: "email", sortable: true },
        { text: "Block", value: "block", sortable: true, align: "center" },
        { text: "+ Begleitung", value: "companion", sortable: false },
        { text: "Angemeldet am", value: "registeredAt", sortable: true },
        { text: "Aktion", value: "actions", sortable: false, align: "right" },
      ],
    }
  },
  computed: {
    // Dynamischer Filter für die Tabelle anhand des Buttons
    filteredRegistrations() {
      if (this.statusFilter === "all") return this.registrations
      return this.registrations.filter((reg) => reg.status === this.statusFilter)
    },
  },
  created() {
    this.loadData()
  },
  methods: {
    async togglePayment(item, type) {
      try {
        let payload = {
          hasPaid: item.hasPaid,
          companionHasPaid: item.companionHasPaid,
        }

        if (type === "main") {
          payload.hasPaid = !item.hasPaid
        } else if (type === "companion") {
          payload.companionHasPaid = !item.companionHasPaid
        }

        await api.updateHyroxPayment(item._id, payload)

        // Optimistisches Update in der UI für flüssiges Erlebnis
        if (type === "main") item.hasPaid = payload.hasPaid
        if (type === "companion") item.companionHasPaid = payload.companionHasPaid

        this.$toast.success("Zahlungsstatus gespeichert.")
      } catch (error) {
        this.$toast.error("Fehler beim Speichern der Zahlung.")
      }
    },
    async loadData() {
      this.loading = true
      try {
        const response = await api.fetchHyroxRegistrations()
        this.registrations = response.registrations
        this.stats = response.stats
      } catch (error) {
        console.error("Fehler beim Laden:", error)
        this.$toast.error("Konnte Daten nicht laden.")
      } finally {
        this.loading = false
      }
    },

    async confirmDelete(item) {
      if (confirm(`Möchtest du die Anmeldung von ${item.firstName} ${item.lastName} wirklich endgültig löschen?`)) {
        try {
          await api.deleteHyroxRegistration(item._id)
          this.$toast.success("Eintrag gelöscht.")
          this.loadData()
        } catch (error) {
          this.$toast.error("Fehler beim Löschen.")
        }
      }
    },

    // CSV Download Methode
    exportCSV() {
      // Wir holen uns NUR die aktiven Teilnehmer
      const activeRegs = this.registrations.filter((r) => r.status === "active")

      if (activeRegs.length === 0) {
        this.$toast.info("Keine aktiven Teilnehmer zum Exportieren.")
        return
      }

      // BOM für Excel hinzufügen
      let csvContent = "\uFEFF"
      // Header um Mitglied/Bezahlt erweitern
      csvContent +=
        "Vorname,Nachname,E-Mail,Geburtsdatum,Mitglied,Bezahlt,Block,Begleitung Vorname,Begleitung Nachname,Begleitung E-Mail,Begleitung Geburtsdatum,Begleitung Mitglied,Begleitung Bezahlt,Angemeldet am\n"

      activeRegs.forEach((reg) => {
        const date = this.$moment(reg.registeredAt).format("DD.MM.YYYY HH:mm")

        const birthMain = reg.birthdate || ""
        const birthComp = reg.hasCompanion && reg.companionBirthdate ? reg.companionBirthdate : ""

        // Begleitungs-Daten sicher auslesen
        const compFirst = reg.hasCompanion ? reg.companionFirstName : ""
        const compLast = reg.hasCompanion ? reg.companionLastName : ""
        const compEmail = reg.hasCompanion && reg.companionEmail ? reg.companionEmail : ""

        // Status auf "Ja"/"Nein"/"Gratis" mappen für die Tabelle
        const memberMain = reg.isMember ? "Ja" : "Nein"
        const paidMain = reg.isMember ? "Gratis" : reg.hasPaid ? "Ja" : "Nein"

        const memberComp = reg.hasCompanion ? (reg.companionIsMember ? "Ja" : "Nein") : ""
        const paidComp = reg.hasCompanion ? (reg.companionIsMember ? "Gratis" : reg.companionHasPaid ? "Ja" : "Nein") : ""

        // Alle Felder einsetzen
        const row = `"${reg.firstName}","${reg.lastName}","${reg.email}","${birthMain}","${memberMain}","${paidMain}","${reg.block}","${compFirst}","${compLast}","${compEmail}","${birthComp}","${memberComp}","${paidComp}","${date}"`
        csvContent += row + "\n"
      })

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")

      link.setAttribute("href", url)
      link.setAttribute("download", `hyrox_pft_teilnehmer_${this.$moment().format("YYYY-MM-DD")}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
  },
}
</script>

<style scoped>
.stats-card {
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5) !important;
}

.neon-border-card {
  border: 1px solid rgba(0, 150, 63, 0.3) !important;
  border-radius: 12px;
  box-shadow: 0 0 15px rgba(0, 150, 63, 0.1);
}

.transparent-table {
  background-color: transparent !important;
}
.transparent-table >>> .v-data-table__wrapper {
  background-color: transparent !important;
}
.transparent-table >>> tr:hover {
  background-color: rgba(0, 150, 63, 0.05) !important;
}

.pulse-animation {
  animation: pulse-button 2s infinite;
  box-shadow: 0 0 0 0 rgba(0, 150, 63, 0.7);
}

@keyframes pulse-button {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 150, 63, 0.7);
  }
  70% {
    transform: scale(1.02);
    box-shadow: 0 0 0 15px rgba(0, 150, 63, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 150, 63, 0);
  }
}

.cursor-pointer {
  cursor: pointer;
}
</style>
